import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import {
  Alert,
  AttachmentField,
  AttachmentGallery,
  Badge,
  Button,
  Card,
  DataList,
  DataRow,
  DateField,
  Filters,
  Kanban,
  KanbanCardBody,
  KanbanStagesModal,
  KanbanTag,
  Modal,
  ModalActions,
  NotProvided,
  Page,
  PageHeader,
  Table,
  TextArea,
  Toolbar,
  readAttachmentContent,
  useAttachments,
  type AttachmentItem,
  type FilterValues,
  type KanbanMove,
  type KanbanStage,
  type KanbanStageActions,
  type StageEventsConfig,
} from '@evogard/ui';
import {
  SALE_EXPENSE_ACT_ACTIONS,
  SALE_EXPENSE_FORMS,
  PAYMENT_STAGE_EVENT_LABELS,
  PAYMENT_STAGE_EVENT_TYPES,
  SALE_EXPENSES_DISABLED_REASON,
  SALE_EXPENSE_STATUS_LABELS,
  SALE_EXPENSE_STATUS_TONES,
  SALE_EXPENSE_TYPES,
  SALE_EXPENSE_TYPE_LABELS,
  SALE_EXPENSE_TYPE_SHORT_LABELS,
  businessDate,
  format,
  formatDate,
  formatInstant,
  fromColumn,
  loadFailureMessage,
  saleExpenseActFor,
  saleExpenseTransition,
  type PaymentStageEventType,
  type SaleExpenseAct,
  type SaleExpenseStatus,
  type SaleExpenseType,
} from '@evogard/domain';
import type {
  AttachmentUrl,
  SaleExpense,
  SaleExpenseAction,
  SaleExpenseDetail,
  SaleExpensesPage,
  SaleExpenseStages,
} from '@evogard/contract';
import { BANKS } from '../../components/beneficiary/banks.js';
import { api, errorMessage, ok } from '../../lib/api.js';
import { useDebouncedTerm } from '../../lib/debounce.js';
import { can, useSessionContext } from '../../lib/session.js';
import { SaleExpenseFormModal } from './SaleExpenseFormModal.js';

const PAGE_SIZE = 50;

/** `?card=` abre um cartão — é o destino do clique no aviso do sino. */
export const saleExpenseCardSearch = z.object({
  card: z.coerce.number().int().positive().optional().catch(undefined),
});

/**
 * De quem é o quadro: da PRAÇA (o Financeiro, `sales.expenses:ver`) ou das
 * PRÓPRIAS despesas (`:ver_minhas`). As rotas da API são outras em cada um — o
 * consultor nunca lê a praça por um filtro no cliente.
 */
type Scope = 'praca' | 'minhas';
type View = 'quadro' | 'tabela';
type BoardAct = Exclude<SaleExpenseAct, 'corrigir'>;

/**
 * As fases do quadro como a praça as configurou — o nome, a cor e os eventos
 * de cada uma (02/10/2026, o mesmo editor de Pagamentos Recorrência). As cinco
 * são sempre as do caminho (`SALE_EXPENSE_MOVES`), na mesma ordem; o que vem da
 * API é a aparência e o que fica pendurado em cada uma. O consultor lê pela
 * rota dele, só nome e cor.
 */
function useSaleExpenseStages(scope: Scope) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  return useQuery({
    queryKey: ['sale-expenses', 'stages', scope, tenantId],
    queryFn: async (): Promise<SaleExpenseStages> => {
      const failure = 'Não foi possível carregar as fases do quadro.';
      if (scope === 'praca') return ok(await api.saleExpenses.stages(), 200, failure);
      const body = ok(await api.saleExpenses.myStages(), 200, failure);
      return {
        stages: body.stages.map((s) => ({ ...s, customized: true, events: [] })),
        accounts: [],
      };
    },
  });
}

/** O nome da fase — o que a praça gravou, ou o de nascença enquanto as fases não chegaram. */
function useStageName(scope: Scope): (status: SaleExpenseStatus) => string {
  const { data } = useSaleExpenseStages(scope);
  return (status) =>
    data?.stages.find((s) => s.status === status)?.name ??
    SALE_EXPENSE_STATUS_LABELS[status];
}

/**
 * O catálogo e os destinos do editor de eventos — o mesmo de Pagamentos
 * Recorrência. A conta arquivada depois de escolhida continua na lista, para o
 * evento gravado não aparecer com o número no lugar do nome.
 */
function eventsConfig(data: SaleExpenseStages): StageEventsConfig {
  const archived = new Map<number, string>();
  for (const s of data.stages) {
    for (const e of s.events) {
      if (e.accountArchived) archived.set(e.accountId, e.accountName);
    }
  }
  return {
    catalog: PAYMENT_STAGE_EVENT_TYPES.map((type) => ({
      type,
      label: PAYMENT_STAGE_EVENT_LABELS[type],
    })),
    targets: [
      ...data.accounts.map((a) => ({ id: String(a.id), label: a.name })),
      ...[...archived].map(([id, name]) => ({
        id: String(id),
        label: `${name} (arquivada)`,
      })),
    ],
    targetLabel: 'Conta do Conta Azul',
    notice: (
      <>
        Os eventos ficam gravados na fase, e <strong>nenhum executa ainda</strong>: o que
        cada um faz no Conta Azul ainda não foi definido. Mover o cartão continua só
        mudando a fase.
      </>
    ),
  };
}

interface Grants {
  check: boolean;
  giveBack: boolean;
  pay: boolean;
}

function granted(grants: Grants, act: BoardAct): boolean {
  switch (SALE_EXPENSE_ACT_ACTIONS[act]) {
    case 'conferir':
      return grants.check;
    case 'devolver':
      return grants.giveBack;
    case 'pagar':
      return grants.pay;
    default:
      return false;
  }
}

/** O ato que o Financeiro abriu, esperando o formulário — ou só a confirmação. */
interface PendingAct {
  act: BoardAct;
  expense: SaleExpense;
  settle?: { resolve: () => void; reject: (e: Error) => void };
}

/**
 * Comercial › Despesas de Venda (e Minhas despesas) — a despesa que o
 * consultor lança a partir de uma venda, da chegada ao comprovante
 * (02/10/2026).
 *
 * ## As fases são fixas, e o caminho também
 *
 * Despesas Recebidas → Em Conferência → Aguardando Pagamento → Pago, e de Em
 * Conferência a Pendente de Correção, com o motivo. Arrastar É o ato: soltar
 * em "Pendente de Correção" pede o motivo; em "Pago", a data e o comprovante.
 * Arrasto fora do caminho desfaz com a frase que diz por quê. O quadro não
 * reenvia a correção — quem a faz é o consultor, pelo formulário.
 *
 * ## O quadro do consultor é o mesmo desenho, só leitura
 *
 * `scope="minhas"` lê `/sales/expenses/mine`: as despesas das vendas dele (lançadas por ele ou por outra pessoa), sem arrasto.
 * O cartão aberto em "Pendente de Correção" tem o botão de corrigir.
 *
 * ## O "…" de cada coluna edita o nome, a cor e os eventos
 *
 * Para quem tem `sales.expense_stages:editar`. As cinco fases continuam as do
 * caminho — não se cria, arquiva nem reordena. Os eventos são gravados e não
 * executam; o editor diz isso. Na visão em tabela, que não tem coluna, o mesmo
 * editor abre pelo botão "Fases" da barra (`KanbanStagesModal`, 08/10/2026).
 */
/**
 * O quadro, ou o motivo de ele estar desativado.
 *
 * Desativada (23/10/2026), a despesa de venda sai do menu, e quem chega aqui
 * por um link salvo ou pelo sino lê o porquê — e não um quadro em que nenhum
 * movimento passa. Ver `SALE_EXPENSES_DISABLED_REASON`.
 */
export function SaleExpensesScreen({ scope }: { scope: Scope }) {
  if (SALE_EXPENSES_DISABLED_REASON === null) return <SaleExpensesBoard scope={scope} />;
  return (
    <Page measure="cheia">
      <PageHeader title={scope === 'minhas' ? 'Minhas despesas' : 'Despesas de Venda'}>
        Indicação, proporcional de troca, vistoria extra, inativo e reembolso.
      </PageHeader>
      <Alert tone="alerta" title="Despesas de venda desativadas">
        {SALE_EXPENSES_DISABLED_REASON} Até lá, nenhuma despesa é lançada, corrigida ou
        paga por aqui. O que já foi lançado continua guardado.
      </Alert>
    </Page>
  );
}

function SaleExpensesBoard({ scope }: { scope: Scope }) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const mine = scope === 'minhas';
  const grants: Grants = {
    check: can(context, 'sales.expenses', 'conferir'),
    giveBack: can(context, 'sales.expenses', 'devolver'),
    pay: can(context, 'sales.expenses', 'pagar'),
  };
  const canMove = !mine && (grants.check || grants.giveBack || grants.pay);

  const queryClient = useQueryClient();
  const stagesQuery = useSaleExpenseStages(scope);
  const stageName = useStageName(scope);
  const canEditStages = !mine && can(context, 'sales.expense_stages', 'editar');
  const stages: KanbanStage[] = (stagesQuery.data?.stages ?? []).map((s) => ({
    id: s.status,
    name: s.name,
    color: s.color,
    events: s.events.map((e) => ({
      id: String(e.id),
      type: e.type,
      targetId: String(e.accountId),
    })),
  }));
  const stageActions: KanbanStageActions | undefined = canEditStages
    ? {
        update: async (stageId, input) => {
          const saved = ok(
            await api.saleExpenses.saveStage({
              params: { status: stageId as SaleExpenseStatus },
              body: {
                name: input.name,
                color: input.color,
                events: (input.events ?? []).map((e) => ({
                  type: e.type as PaymentStageEventType,
                  accountId: Number(e.targetId),
                })),
              },
            }),
            200,
            'Não foi possível salvar a fase.',
          );
          queryClient.setQueryData(['sale-expenses', 'stages', scope, tenantId], saved);
        },
      }
    : undefined;

  const search = useSearch({ strict: false }) as { card?: number };
  const navigate = useNavigate();
  const openId = search.card ?? null;
  const setOpenId = (id: number | null) =>
    void navigate({
      to: mine ? '/sales/expenses/mine' : '/sales/expenses',
      search: id === null ? {} : { card: id },
      replace: true,
    });

  const [view, setView] = useState<View>('quadro');
  const [searchText, setSearchText] = useState('');
  const term = useDebouncedTerm(searchText.trim());
  const [filters, setFilters] = useState<FilterValues>({});
  /* Vários tipos de uma vez — vazio é todos. Vai à API separado por vírgula. */
  const chosenTypes = (Array.isArray(filters.types) ? filters.types : []).filter(
    (t): t is SaleExpenseType => (SALE_EXPENSE_TYPES as readonly string[]).includes(t),
  );
  const types = chosenTypes.length > 0 ? chosenTypes.join(',') : undefined;

  const [version, setVersion] = useState(0);
  const refresh = () => setVersion((v) => v + 1);
  const known = useRef(new Map<string, SaleExpense>());
  const [pending, setPending] = useState<PendingAct | null>(null);
  const [stagesOpen, setStagesOpen] = useState(false);

  const listQuery = { types, search: mine ? undefined : term || undefined };

  const fetchPage = async (
    query: typeof listQuery & {
      status?: SaleExpenseStatus;
      cursor?: string;
      perPage: number;
    },
    failure: string,
  ): Promise<SaleExpensesPage> => {
    if (!mine) return ok(await api.saleExpenses.list({ query }), 200, failure);
    const { search: _ignored, ...own } = query;
    return ok(await api.saleExpenses.mine({ query: own }), 200, failure);
  };

  const loadPage = async (stageId: string, cursor: string | null) => {
    const body = await fetchPage(
      {
        ...listQuery,
        status: stageId as SaleExpenseStatus,
        cursor: cursor ?? undefined,
        perPage: PAGE_SIZE,
      },
      'Não foi possível carregar esta fase.',
    );
    for (const e of body.items) known.current.set(String(e.id), e);
    return { items: body.items, next: body.next, total: body.total };
  };

  const moveCard = (move: KanbanMove): Promise<void> | void => {
    if (move.fromStageId === move.toStageId) return;
    const expense = known.current.get(move.cardId);
    const to = move.toStageId as SaleExpenseStatus;
    const act = expense
      ? saleExpenseActFor(move.fromStageId as SaleExpenseStatus, to)
      : null;
    if (!expense || act === null) {
      throw new Error(
        move.fromStageId === 'pendente_correcao'
          ? 'Quem tira da correção é o consultor, reenviando a despesa corrigida.'
          : `Daqui o cartão não vai para "${stageName(to)}".`,
      );
    }
    if (!granted(grants, act)) {
      throw new Error(
        act === 'pagar'
          ? 'Marcar como pago pede outra permissão, que você não tem.'
          : act === 'devolver'
            ? 'Devolver ao consultor pede outra permissão, que você não tem.'
            : 'Você não tem permissão para este passo.',
      );
    }
    return new Promise<void>((resolve, reject) => {
      setPending({ act, expense, settle: { resolve, reject } });
    });
  };

  const table = useQuery({
    queryKey: ['sale-expenses', scope, 'table', tenantId, listQuery, version],
    enabled: view === 'tabela',
    queryFn: () =>
      fetchPage(
        { ...listQuery, perPage: 200 },
        loadFailureMessage(undefined, 'as despesas'),
      ),
  });

  const filtered = Boolean(term || types);

  return (
    <Page measure="cheia">
      <PageHeader title={mine ? 'Minhas despesas' : 'Despesas de Venda'}>
        {mine
          ? 'As despesas das suas vendas — lançadas por você ou por outra pessoa —, até o pagamento. Uma despesa nova é lançada nas ações da venda, em Vendas.'
          : 'Indicação, proporcional de troca, vistoria extra, inativo e reembolso — lançados pelos consultores a partir das vendas, até o comprovante.'}
      </PageHeader>

      <Toolbar
        search={
          mine
            ? undefined
            : {
                label: 'Pesquisar despesa',
                placeholder: 'Placa, nome ou consultor',
                value: searchText,
                onChange: setSearchText,
              }
        }
        toolbar={
          <>
            <Filters
              values={filters}
              onApply={setFilters}
              fields={[
                {
                  type: 'multipla',
                  name: 'types',
                  label: 'Tipos de despesa',
                  placeholder: 'Todos os tipos',
                  options: SALE_EXPENSE_TYPES.map((t) => ({
                    value: t,
                    label: SALE_EXPENSE_TYPE_SHORT_LABELS[t],
                  })),
                },
              ]}
            />
            {/* No quadro a fase se edita pelo "…" da coluna; a tabela não tem
                coluna, e o caminho até o mesmo editor é este. */}
            {view === 'tabela' && stageActions?.update && stagesQuery.data && (
              <Button
                variant="contorno"
                icon="etiqueta"
                onClick={() => setStagesOpen(true)}
              >
                Fases
              </Button>
            )}
            <Button
              variant="contorno"
              icon={view === 'quadro' ? 'documento' : 'quadro'}
              onClick={() => setView(view === 'quadro' ? 'tabela' : 'quadro')}
            >
              {view === 'quadro' ? 'Ver tabela' : 'Ver quadro'}
            </Button>
          </>
        }
      />

      {view === 'quadro' && !stagesQuery.data ? (
        stagesQuery.isError ? (
          <Alert tone="erro">{stagesQuery.error.message}</Alert>
        ) : (
          <p className="text-sm text-texto-suave">Carregando o quadro…</p>
        )
      ) : view === 'quadro' ? (
        <Kanban<SaleExpense>
          label={mine ? 'Quadro das minhas despesas' : 'Quadro das despesas de venda'}
          stages={stages}
          stageActions={stageActions}
          stageEvents={
            !mine && stagesQuery.data ? eventsConfig(stagesQuery.data) : undefined
          }
          cardId={(e) => String(e.id)}
          cardLabel={(e) =>
            `${SALE_EXPENSE_TYPE_LABELS[e.type]}, placa ${e.plate}, ${format(fromColumn(e.amount)!)}`
          }
          loadPage={loadPage}
          reloadKey={JSON.stringify([tenantId, scope, version, listQuery])}
          renderCard={(e) => <ExpenseCardBody expense={e} mine={mine} />}
          onMoveCard={canMove ? moveCard : undefined}
          onOpenCard={(e) => setOpenId(e.id)}
          emptyColumnText={
            filtered
              ? 'Nenhuma despesa desta fase com esses filtros.'
              : 'Nenhuma despesa nesta fase.'
          }
        />
      ) : (
        <Card bleed>
          {table.isError && <Alert tone="erro">{table.error.message}</Alert>}
          <Table
            label={mine ? 'Minhas despesas' : 'Despesas de venda'}
            items={table.data?.items ?? []}
            rowKey={(e) => String(e.id)}
            rowActions={(e) => [
              { label: 'Abrir', icon: 'olho', run: () => setOpenId(e.id) },
            ]}
            empty={
              table.isPending
                ? 'Carregando…'
                : mine
                  ? 'Nenhuma despesa nas suas vendas. Ela é lançada nas ações de uma venda, em Vendas.'
                  : 'Nenhuma despesa lançada. O cartão aparece aqui quando um consultor lança a despesa de uma venda.'
            }
            columns={[
              {
                title: 'Tipo',
                minWidth: '10rem',
                cell: (e) => SALE_EXPENSE_TYPE_SHORT_LABELS[e.type],
              },
              { title: 'Placa', minWidth: '6rem', cell: (e) => e.plate },
              { title: 'Nome', minWidth: '12rem', cell: (e) => e.personName },
              {
                title: 'Associado da venda',
                minWidth: '12rem',
                cell: (e) =>
                  memberLabel(e) ?? <span className="text-texto-suave">—</span>,
              },
              ...(mine
                ? []
                : [
                    {
                      title: 'Consultor',
                      minWidth: '12rem',
                      cell: (e: SaleExpense) => e.consultant.nome,
                    },
                  ]),
              {
                title: 'Valor',
                minWidth: '8rem',
                alignment: 'fim' as const,
                cell: (e) => format(fromColumn(e.amount)!),
              },
              {
                title: 'Fase',
                minWidth: '11rem',
                cell: (e) => (
                  <StatusBadge status={e.status} label={stageName(e.status)} />
                ),
              },
              {
                title: 'Lançada em',
                minWidth: '9rem',
                cell: (e) => formatInstant(e.createdAt),
              },
            ]}
          />
          {table.data && table.data.total > table.data.items.length && (
            <p className="px-4 pb-4 text-sm text-texto-suave">
              Mostrando {table.data.items.length} de {table.data.total}. Use os filtros
              para chegar às outras.
            </p>
          )}
        </Card>
      )}

      {openId !== null && (
        <ExpenseDetailModal
          id={openId}
          mine={mine}
          grants={grants}
          canCorrect={can(context, 'sales.expenses', 'lancar')}
          onClose={() => setOpenId(null)}
          onAct={(act, expense) => setPending({ act, expense })}
          onChanged={refresh}
        />
      )}

      {pending && (
        <ActModal
          pending={pending}
          onDone={() => {
            pending.settle?.resolve();
            setPending(null);
            refresh();
          }}
          onCancel={() => {
            pending.settle?.reject(
              new Error('Nada mudou: o cartão voltou para onde estava.'),
            );
            setPending(null);
          }}
        />
      )}

      {stageActions?.update && stagesQuery.data && (
        <KanbanStagesModal
          open={stagesOpen}
          onOpenChange={setStagesOpen}
          stages={stages}
          update={stageActions.update}
          stageEvents={eventsConfig(stagesQuery.data)}
          emptyColumnText="Nenhuma despesa nesta fase."
        />
      )}
    </Page>
  );
}

function StatusBadge({ status, label }: { status: SaleExpenseStatus; label?: string }) {
  return (
    <Badge tone={SALE_EXPENSE_STATUS_TONES[status]}>
      {label ?? SALE_EXPENSE_STATUS_LABELS[status]}
    </Badge>
  );
}

/** O associado da venda — o nome, ou o código cru quando o rótulo não veio. */
function memberLabel(e: SaleExpense): string | null {
  if (!e.member) return null;
  return e.member.nome ?? `código ${e.member.code}`;
}

function vehicleLabel(e: SaleExpense): string | null {
  if (!e.vehicle) return null;
  return [e.vehicle.marca, e.vehicle.modelo].filter(Boolean).join(' ') || null;
}

function bankLabel(code: string | null): string | null {
  if (code === null) return null;
  const bank = BANKS.find((b) => b.code === code);
  return bank ? `${bank.code} · ${bank.name}` : code;
}

/** O miolo do cartão: o tipo, o veículo, o associado e a venda. */
function ExpenseCardBody({ expense: e, mine }: { expense: SaleExpense; mine: boolean }) {
  const vehicle = vehicleLabel(e);
  const member = memberLabel(e);
  return (
    <KanbanCardBody
      title={SALE_EXPENSE_TYPE_LABELS[e.type]}
      tags={
        <>
          <KanbanTag>{e.plate}</KanbanTag>
          {vehicle && <KanbanTag>{vehicle}</KanbanTag>}
          {!mine && <KanbanTag>{e.consultant.nome}</KanbanTag>}
          {e.status === 'pendente_correcao' && (
            <KanbanTag tone="erro">Devolvida</KanbanTag>
          )}
        </>
      }
      detail={
        `${format(fromColumn(e.amount)!)} · ${e.personName}` +
        (member ? ` · associado ${member}` : '') +
        ` · venda ${monthLabel(e.sale.period)}`
      }
      detailIcon="dinheiro"
    />
  );
}

/* ------------------------------------------------------------------------- *
 * O cartão aberto
 * ------------------------------------------------------------------------- */

function ExpenseDetailModal({
  id,
  mine,
  grants,
  canCorrect,
  onClose,
  onAct,
  onChanged,
}: {
  id: number;
  mine: boolean;
  grants: Grants;
  canCorrect: boolean;
  onClose: () => void;
  onAct: (act: BoardAct, expense: SaleExpense) => void;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const detailStageName = useStageName(mine ? 'minhas' : 'praca');
  const key = ['sale-expenses', mine ? 'minhas' : 'praca', 'detail', id];
  const query = useQuery({
    queryKey: key,
    queryFn: async () =>
      ok(
        mine
          ? await api.saleExpenses.myDetail({ params: { id } })
          : await api.saleExpenses.detail({ params: { id } }),
        200,
        'Não foi possível abrir a despesa.',
      ),
  });
  const d = query.data;
  const [correcting, setCorrecting] = useState(false);

  const actions: { act: BoardAct; label: string; variant?: 'contorno' }[] = [];
  if (d && !mine) {
    if (grants.giveBack && saleExpenseTransition(d.status, 'devolver')) {
      actions.push({
        act: 'devolver',
        label: 'Devolver ao consultor',
        variant: 'contorno',
      });
    }
    if (grants.check && saleExpenseTransition(d.status, 'conferir')) {
      actions.push({ act: 'conferir', label: 'Receber para conferência' });
    }
    if (grants.check && saleExpenseTransition(d.status, 'aprovar')) {
      actions.push({ act: 'aprovar', label: 'Aprovar para pagamento' });
    }
    if (grants.pay && saleExpenseTransition(d.status, 'pagar')) {
      actions.push({ act: 'pagar', label: 'Marcar como pago' });
    }
  }

  const sign = async (fileId: number): Promise<AttachmentUrl> =>
    ok(
      mine
        ? await api.saleExpenses.myFileUrl({ params: { id, fileId } })
        : await api.saleExpenses.fileUrl({ params: { id, fileId } }),
      200,
      'Não foi possível abrir o arquivo.',
    );

  if (correcting && d) {
    return (
      <SaleExpenseFormModal
        mode={{ kind: 'corrigir', expense: d }}
        onClose={() => setCorrecting(false)}
        onSaved={(saved) => {
          queryClient.setQueryData(key, saved);
          setCorrecting(false);
          onChanged();
        }}
      />
    );
  }

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={d ? `${SALE_EXPENSE_TYPE_LABELS[d.type]} — ${d.plate}` : 'Despesa'}
      descricao={
        d
          ? `Consultor ${d.consultant.nome} · lançada${d.launchedBy.id === d.consultant.id ? '' : ` por ${d.launchedBy.nome}`} em ${formatInstant(d.createdAt)}`
          : undefined
      }
      size="lg"
    >
      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {d && (
        <div className="flex flex-col gap-5">
          {d.status === 'pendente_correcao' && (
            <Alert
              tone="alerta"
              title={mine ? 'O Financeiro pediu correção' : 'Devolvida ao consultor'}
            >
              {d.correctionReason ?? 'O motivo não foi registrado.'}
            </Alert>
          )}

          <DataList>
            <DataRow label="Fase">
              <StatusBadge status={d.status} label={detailStageName(d.status)} />
            </DataRow>
            <DataRow label={SALE_EXPENSE_FORMS[d.type].dateLabel}>
              {formatDate(d.referenceDate)}
            </DataRow>
            <DataRow label="Placa">{d.plate}</DataRow>
            <DataRow label={SALE_EXPENSE_FORMS[d.type].personLabel}>
              {d.personName}
            </DataRow>
            {d.contact && <DataRow label="Contato">{d.contact}</DataRow>}
            <DataRow label={SALE_EXPENSE_FORMS[d.type].amountLabel}>
              {format(fromColumn(d.amount)!)}
            </DataRow>
            {d.pixKey && <DataRow label="Chave PIX">{d.pixKey}</DataRow>}
            {d.pixBankCode && (
              <DataRow label="Banco de origem do PIX">{bankLabel(d.pixBankCode)}</DataRow>
            )}
            {d.cancellationReason && (
              <DataRow label="Motivo do cancelamento">{d.cancellationReason}</DataRow>
            )}
            <DataRow label="Consultor">{d.consultant.nome}</DataRow>
            {d.launchedBy.id !== d.consultant.id && (
              <DataRow label="Lançada por">{d.launchedBy.nome}</DataRow>
            )}
            {d.paidOn && <DataRow label="Pago em">{formatDate(d.paidOn)}</DataRow>}
          </DataList>

          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-bold">A venda</h3>
            <DataList>
              <DataRow label="Competência">{monthLabel(d.sale.period)}</DataRow>
              <DataRow label="Associado">
                {memberLabel(d) ?? (
                  <NotProvided>o cadastro do ERP não respondeu</NotProvided>
                )}
              </DataRow>
              <DataRow label="Veículo">
                {d.vehicle ? (
                  [d.vehicle.placa, vehicleLabel(d)].filter(Boolean).join(' · ')
                ) : (
                  <NotProvided>o cadastro do ERP não respondeu</NotProvided>
                )}
              </DataRow>
              <DataRow label="Valor da venda">
                {d.sale.value === null
                  ? 'a origem não disse'
                  : format(fromColumn(d.sale.value)!)}
              </DataRow>
              <DataRow label="Origem">
                {d.sale.idExterno === null
                  ? 'registrada aqui'
                  : `importada · ${d.sale.idExterno}`}
              </DataRow>
            </DataList>
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-bold">Arquivos</h3>
            <FileList detail={d} sign={sign} />
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-bold">Histórico</h3>
            <ol className="flex flex-col gap-2 text-sm">
              {d.transitions.map((t, i) => (
                <li key={i}>
                  <span className="font-medium">
                    {t.from === null ? 'Lançada' : SALE_EXPENSE_STATUS_LABELS[t.to]}
                  </span>
                  <span className="text-texto-suave">
                    {' '}
                    · {formatInstant(t.at)}
                    {t.by ? ` · ${t.by}` : ''}
                  </span>
                  {t.note && <p className="text-texto-suave">{t.note}</p>}
                </li>
              ))}
            </ol>
          </section>

          {mine && d.status === 'pendente_correcao' && (
            <ModalActions>
              {canCorrect ? (
                <Button onClick={() => setCorrecting(true)}>Corrigir e reenviar</Button>
              ) : (
                <NotProvided>
                  Você acompanha esta despesa, mas corrigir pede outra permissão. Fale com
                  quem administra os grupos.
                </NotProvided>
              )}
            </ModalActions>
          )}

          {actions.length > 0 && (
            <ModalActions>
              {actions.map((a) => (
                <Button key={a.act} variant={a.variant} onClick={() => onAct(a.act, d)}>
                  {a.label}
                </Button>
              ))}
            </ModalActions>
          )}
        </div>
      )}
    </Modal>
  );
}

/**
 * Os arquivos, do mais novo ao mais velho, na grade de anexos da casa
 * (`AttachmentGallery`): miniatura e, no clique, o visualizador por cima da
 * tela — sem abrir outra aba. A URL é assinada e vence, então vira
 * `resolveUrl`, pedida por quem desenha.
 *
 * `sign` mora numa ref porque o modal a recria a cada render: uma `resolveUrl`
 * nova refaz o efeito de `useSource`, que pede a URL, que re-renderiza — um
 * laço de requisições.
 */
function FileList({
  detail: d,
  sign,
}: {
  detail: SaleExpenseDetail;
  sign: (fileId: number) => Promise<AttachmentUrl>;
}) {
  const signRef = useRef(sign);
  signRef.current = sign;
  const { files, type } = d;

  const items = useMemo<AttachmentItem[]>(() => {
    const latest = files.find((f) => f.kind === 'comprovante')?.id;
    return files.map((f) => ({
      key: String(f.id),
      nome: f.nome,
      typeMime: f.typeMime,
      sizeBytes: f.sizeBytes,
      detail: [
        f.kind === 'pagamento'
          ? 'Comprovante de pagamento'
          : SALE_EXPENSE_FORMS[type].attachmentLabel,
        formatInstant(f.createdAt),
        f.kind === 'comprovante' && f.id !== latest ? 'substituído na correção' : null,
      ]
        .filter(Boolean)
        .join(' · '),
      resolveUrl: async () => (await signRef.current(f.id)).url,
    }));
  }, [files, type]);

  return (
    <AttachmentGallery items={items} empty={<NotProvided>Nenhum arquivo.</NotProvided>} />
  );
}

/* ------------------------------------------------------------------------- *
 * Os atos do Financeiro
 * ------------------------------------------------------------------------- */

// @valor
const ACT_TITLES: Record<BoardAct, string> = {
  conferir: 'Receber para conferência',
  aprovar: 'Aprovar para pagamento',
  devolver: 'Devolver ao consultor',
  pagar: 'Marcar como pago',
};

function ActModal({
  pending,
  onDone,
  onCancel,
}: {
  pending: PendingAct;
  onDone: () => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const { act, expense: e } = pending;
  const [reason, setReason] = useState('');
  const [paidOn, setPaidOn] = useState(businessDate(new Date()));
  const receipt = useAttachments();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      let body: SaleExpenseAction;
      if (act === 'devolver') body = { action: 'devolver', reason: reason.trim() };
      else if (act === 'pagar') {
        const file = receipt.items[0]!;
        body = {
          action: 'pagar',
          paidOn,
          receipt: {
            nome: file.nome,
            typeMime: file.typeMime,
            content: await readAttachmentContent(file.file),
          },
        };
      } else body = { action: act };
      const r = await api.saleExpenses.act({ params: { id: e.id }, body });
      if (r.status !== 200) throw new Error(errorMessage(r.body));
      queryClient.setQueryData(['sale-expenses', 'praca', 'detail', e.id], r.body);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Algo deu errado.');
    } finally {
      setBusy(false);
    }
  };

  const ready =
    act === 'devolver'
      ? reason.trim().length >= 3
      : act === 'pagar'
        ? receipt.items.length === 1 && Boolean(paidOn)
        : true;

  return (
    <Modal
      open
      onOpenChange={(open) => !open && !busy && onCancel()}
      title={ACT_TITLES[act]}
      descricao={`${SALE_EXPENSE_TYPE_LABELS[e.type]} · placa ${e.plate} · ${e.consultant.nome} · ${format(fromColumn(e.amount)!)}`}
      size="md"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(ev) => {
          ev.preventDefault();
          if (ready) void submit();
        }}
      >
        {act === 'conferir' && (
          <p className="text-sm">A despesa passa para "Em Conferência".</p>
        )}
        {act === 'aprovar' && (
          <p className="text-sm">
            A despesa conferida passa para "Aguardando Pagamento".
          </p>
        )}
        {act === 'devolver' && (
          <TextArea
            label="O que o consultor precisa corrigir"
            required
            maxLength={2000}
            value={reason}
            onChange={(ev) => setReason(ev.target.value)}
            help="Ele recebe um aviso com esta frase e corrige pelo quadro dele."
          />
        )}
        {act === 'pagar' && (
          <>
            {e.pixKey && (
              <p className="text-sm">
                PIX: <strong>{e.pixKey}</strong>
                {e.pixBankCode && ` · ${bankLabel(e.pixBankCode)}`}
              </p>
            )}
            <DateField
              label="Data do pagamento"
              required
              value={paidOn}
              onChange={setPaidOn}
              help="A do comprovante. Não pode estar no futuro."
            />
            <AttachmentField
              label="Comprovante"
              items={receipt.items}
              onAdd={receipt.add}
              onRemove={receipt.remove}
              max={1}
              disabled={busy}
            />
          </>
        )}
        {error && <Alert tone="erro">{error}</Alert>}
        <ModalActions>
          <Button variant="discreto" onClick={onCancel} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" loading={busy} disabled={!ready}>
            {ACT_TITLES[act]}
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
}

/** `2026-09` → `09/2026`. */
function monthLabel(period: string): string {
  return `${period.slice(5, 7)}/${period.slice(0, 4)}`;
}
